/**
 * AiDocumentCreatorBridge — host-side handler for "create document" requests
 * coming from the compact docs tab inside the AI assistant iframe.
 *
 * Why this exists:
 *   The compact docs UI (ai_assistent/frontend/src/components/DocsTab.tsx)
 *   does the extraction + matching, but ERP writes live entirely in
 *   nexilcolab — they need:
 *     • the imperative `createRealCustomerDocuments(cid, doc)` API,
 *     • `createDocument(...)` to assemble the ExtendedDocument with the
 *       right per-type shape (order / sup_invoice / delivery_certificate /
 *       …), totals, comment, details, …,
 *     • full IItem rows with `available_units`, `barcode`,
 *       `conversion_ratio`, etc. — which the iframe doesn't have.
 *
 *   This component subscribes to the `ai-host-bridge-request` window event
 *   (dispatched by AiAssistantDrawer when the iframe posts an
 *   `ai-host-bridge`) and, for `op: 'create-document'`, runs the create
 *   end-to-end then dispatches `ai-host-bridge-result` with the outcome.
 *   AiAssistantDrawer relays that back into the iframe so the docs tab
 *   can resolve the awaiting promise and switch the row to "נוצר".
 *
 *   For `op: 'open-in-drawer'` it re-broadcasts a generic
 *   `ai-open-text-to-data` window event. `AiTextToDataBridge` (mounted
 *   alongside this component) picks it up and opens the ERP's native
 *   <TextToDataDrawerAi> smart drawer pre-selected on the right
 *   process/client — this is the legacy fallback path for jobs created
 *   before the drafts pipeline existed; the primary flow today is
 *   `hostBridge.openDraft` → DraftsDrawer (docs-manager view) inside the
 *   chat iframe itself, no host bridge involved.
 *
 *   For `op: 'navigate'` (and the legacy `op: 'open-orders'`) it pushes a
 *   react-router route for a small allowlist of named targets (see
 *   NAVIGATE_ROUTES below) — the agent never sends a raw URL, only a
 *   target key + params, so navigation stays constrained to known pages.
 *
 *   For `op: 'open-send-dialog'` it opens the ERP's native WhatsApp
 *   chat/bulk composer (same `wa-open-chat` / `wa-open-bulk` events the
 *   in-app "שלח הודעה" dialog uses) pre-filled with a client + message,
 *   so a human reviews/attaches docs and hits send themselves — unlike
 *   send-documents/send-message (immediate, no review step).
 *
 * Mounting:
 *   Mounted once in MainLayout alongside <AiAssistantDrawer>, inside the
 *   QueryClient + Redux providers so the imperative API has access to
 *   JSDB() user state, query cache invalidation works, and SNACKBAR
 *   actions can be dispatched. Must also be inside the router (it calls
 *   useNavigate for the `navigate` op) — MainLayout already is.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';

import { dateObjectToString, roundDecimals } from '../../../fromKotlin/nkutils';
import { createDocument } from '../../../types/DocumentsTypes/utils';
import {
    createRealCustomerDocuments,
    createSupplierNote,
    getSpecificDocument,
} from '../../../api/customer/documents.api';
import {
    getCustomerProductsPrices,
    getSupplierProductsPrices,
} from '../../../api/customer/products_prices.api';
import { getSpecificSuppliersAsync } from '../../../api/supplier';
import { getSpecificCustomersAsync } from '../../../api/customer';
import JSDB from '../../../fromKotlin/nk';
import {
    CUSTOMER_DOCUMENTS, SUPPLIER_DOCUMENTS,
} from '../../../hooks/customer/documents.hooks';
import { SNACKBAR_OPEN } from 'store/actions';
import { AI_HOST_BRIDGE_EVENTS, dispatchAiClientDebtRefresh } from './AiAssistantDrawer';
import DocumentPreview from '../../../views/customer/profile/documents/DocumentPreview';
import { exportAiTable, ExportTablePayload } from '../../../utils/aiTableExport';

import type IItem from '../../../types/product_delivery';
import type { DocumentType, ExtendedDocument } from '../../../types/DocumentsTypes/Document';

/**
 * DocumentPreview dialog must sit above the AI iframe shell (~1300–2000) and
 * the iframe's OrderConflictDialog (2600). Autocomplete / Select poppers use
 * theme.zIndex.modal and portal to body — so they need dialogZ + offset or
 * they hide under the chat iframe.
 */
const AI_DOC_PREVIEW_DIALOG_Z = 2700;
const AI_DOC_PREVIEW_POPPER_OFFSET = 100;

/** Payload shape (matches `DocsCreatePayload` in ai_assistent DocsTab.tsx). */
interface IncomingPayload {
    process: string;
    dateIso: string;
    entity: { id: string; name: string | null; externalId: string | null };
    products: Array<{
        productId: number;
        productName: string;
        amount: number;
        unit: string | null;
        price: number | null;
    }>;
    source?: { jobName: string; jobId: string };
}

/** Map the docs-tab `process` enum to (docType, isSupplier). Anything we
 *  don't recognise falls back to a customer "order" so the UI doesn't
 *  silently 500 — the user will see the snackbar say what was created. */
function resolveDocType(process: string): { type: ExtendedDocument['type']; supplier: boolean } {
    switch (process) {
        case 'order':              return { type: 'order',                supplier: false };
        case 'delivery':           return { type: 'delivery_certificate', supplier: false };
        case 'invoice':            return { type: 'tax_invoice',          supplier: false };
        case 'credit_note':        return { type: 'refund',               supplier: false };
        case 'supplier_order':     return { type: 'order',                supplier: true  };
        case 'supplier_delivery':  return { type: 'supplier_note',        supplier: true  };
        case 'supplier_invoice':   return { type: 'sup_invoice',          supplier: true  };
        default:                   return { type: 'order',                supplier: false };
    }
}

/** Fetch full IItem details for the matched productIds. For customers we
 *  use the API's `ids` filter; for suppliers (no filter param) we fetch
 *  all and filter locally — identical strategy to the smart drawer's
 *  `loadMatchedProductsOnly` helper.
 *
 *  Why we pre-load the entity into JSDB first (both branches):
 *    `getCustomerProductsPrices` / `getSupplierProductsPrices` internally
 *    call `itemsApi(client, …)` where `client = JSDB().getClient(cid).first`
 *    (or `getSupplier(...)`). That entity is only in JSDB if some earlier
 *    code fetched it — the existing `NewSupNote` / customer-profile pages
 *    dispatch `getSpecificSupplier(id)` / `getSpecificClient(id)` on
 *    mount. The compact docs tab in the chat doesn't go through those
 *    pages, so without this pre-load JSDB returns null and `itemsApi`
 *    crashes with:
 *      "Cannot read properties of null (reading 'getActiveProductsPrices')".
 *    `FETCH_CLIENT_DATA` / `FETCH_SUPPLIER_DATA` populate JSDB as a
 *    side-effect (same calls the legacy pages use) — short, cached on
 *    the backend, and leave the rest of the flow identical. */
async function loadProductDetails(
    entityId: string,
    dateIso: string,
    productIds: number[],
    supplier: boolean,
): Promise<IItem[]> {
    if (!productIds.length) return [];
    const cid = Number(entityId);
    if (!Number.isFinite(cid) || cid <= 0) return [];

    if (supplier) {
        // Best-effort pre-load. If JSDB already has the supplier (e.g. the
        // user opened a supplier page earlier in the session) the call is
        // still cheap and idempotent on the backend.
        if (!JSDB().getSupplier(cid).first) {
            try { await getSpecificSuppliersAsync(cid); }
            catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[AiDocumentCreatorBridge] FETCH_SUPPLIER_DATA failed', err);
            }
        }
        const all = await getSupplierProductsPrices(entityId, dateIso, true);
        const wanted = new Set(productIds.map(Number));
        return all.filter((p) => wanted.has(Number(p.id)));
    }

    /* Same precondition for clients — without this, opening the chat,
     * extracting an order, and pressing "create" on a customer the user
     * hasn't visited this session crashes inside `itemsApi`. */
    if (!JSDB().getClient(cid).first) {
        try { await getSpecificCustomersAsync(cid); }
        catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[AiDocumentCreatorBridge] FETCH_CLIENT_DATA failed', err);
        }
    }
    return getCustomerProductsPrices(
        entityId, dateIso, true, productIds, false, true,
    );
}

/** Decide which "unit slot" each line uses based on its unit string.
 *  Matches the same idea as `matchedLinesToItems` in features/text-to-data-ai
 *  but works from raw strings the user can have edited in the compact UI.
 *
 *  Defensive on input types: `unitRaw` is *typed* `string | null` but in
 *  practice the iframe can send a number (legacy DocsTab jobs stored a raw
 *  WrappedName state int in `localStorage` before the wrapped-name mapping
 *  was added), and `available_units` on IItem occasionally surfaces a
 *  non-string entry too. We coerce both sides through String(), so a
 *  numeric leak shows the correct slot index instead of throwing
 *  `s.replace is not a function`. */
function pickUnitIndex(product: IItem, unitRaw: unknown): number {
    const norm = (s: unknown) => String(s ?? '').replace(/\s+/g, '').toLowerCase();
    const units = (product.available_units || [])
        .map((u) => String(u ?? ''))
        .filter((u) => u.length > 0);
    const target = norm(unitRaw);
    if (!target || units.length < 2) return 0;
    const idx = units.findIndex((u) => norm(u) === target);
    return idx > 0 ? idx : 0;
}

function buildIItem(
    product: IItem,
    row: IncomingPayload['products'][number],
    index: number,
): IItem {
    const unitIndex = pickUnitIndex(product, row.unit);
    const usesSecond = unitIndex > 0;
    const totalPrice = row.price ?? product.totalPrice;
    return {
        ...product,
        quantity:               usesSecond ? 0 : Number(row.amount || 0),
        quantitySecond:         usesSecond ? Number(row.amount || 0) : 0,
        quantityReturns:        0,
        quantitySecondReturns:  0,
        totalPrice,
        priceTouched: row.price != null && row.price !== product.totalPrice,
        notePlacement: index,
        notes: '',
        conversion_ratio: usesSecond ? (product.conversion_ratio ?? 0) : product.conversion_ratio,
    };
}

function calcTotals(items: IItem[]) {
    const total = items.reduce((sum, item) => {
        const amount = (item.quantity ?? 0) + (item.quantitySecond ?? 0);
        return sum + amount * Number(item.totalPrice ?? 0);
    }, 0);
    return {
        mainValue: roundDecimals(total, 4),
        preValue:  roundDecimals(total, 4),
        taxValue:  0,
    };
}

/** Noop setter for DocumentPreview's `setCreditRecepitValue` — the preview
 *  uses it only in the receipt flow (which we never render from the chat
 *  bridge). Matches the pattern in `views/customer/anomalies/Anomalies.tsx`. */
const NOOP_SET_BOOL: React.Dispatch<React.SetStateAction<boolean>> = () => undefined;

/** Map `DocsTab` process strings to the loader pair needed by
 *  `getSpecificDocument`. Mirrors `resolveDocType` but yields the
 *  *loader* type (which sometimes differs from the create-time type —
 *  e.g. customer orders are created via 'order' but the loader for
 *  `delivery_certificate` is reused for delivery notes). */
function resolvePreviewType(rawType: unknown): { type: DocumentType; supplier: boolean } {
    const process = String(rawType || 'order')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
    switch (process) {
        // ── chat DocProcess vocabulary ──────────────────────────────────
        case 'customer_order':
        case 'sales_order':
        case 'order':              return { type: 'order',                supplier: false };
        case 'customer_delivery':
        case 'delivery_note':
        case 'delivery':           return { type: 'delivery_certificate', supplier: false };
        // getSpecificDocument naming follows the legacy Kotlin API:
        // invoice = tax-note type 0; tax_invoice = invoice-receipt type 1.
        case 'customer_invoice':
        case 'invoice':            return { type: 'invoice',              supplier: false };
        case 'customer_invoice_receipt':
        case 'invoice_receipt':    return { type: 'tax_invoice',          supplier: false };
        case 'payment_receipt':
        case 'pay_note':
        case 'receipt':            return { type: 'receipt',              supplier: false };
        case 'customer_credit':
        case 'credit':
        case 'credit_note':        return { type: 'refund',               supplier: false };
        case 'supplier_order':     return { type: 'supplier_note',        supplier: true  };
        case 'supplier_delivery':  return { type: 'supplier_note',        supplier: true  };
        case 'supplier_invoice':   return { type: 'sup_invoice',          supplier: true  };
        // ── MCP doc_type vocabulary (DraftsDrawer passes approved_doc_type,
        //    which is already the resolved MCP type, not a DocProcess) ────
        case 'delivery_certificate': return { type: 'delivery_certificate', supplier: false };
        case 'tax_invoice':          return { type: 'tax_invoice',          supplier: false };
        case 'refund':               return { type: 'refund',               supplier: false };
        case 'supplier_note':        return { type: 'supplier_note',        supplier: true  };
        case 'sup_invoice':          return { type: 'sup_invoice',          supplier: true  };
        case 'supplier_tax_invoice':
        case 'sup_tax_invoice':      return { type: 'sup_tax_invoice',      supplier: true  };
        case 'supplier_refund':
        case 'sup_refund':           return { type: 'sup_refund',           supplier: true  };
        default:                   return { type: 'order',                supplier: false };
    }
}

/** Allowlist of `navigate` targets → route builder. The agent names a
 *  target key (never a raw URL); unknown keys are ignored (logged) so a
 *  bad/old agent build can't be tricked into arbitrary navigation.
 *  `params` is also passed through as router `state` so a target page can
 *  opt into reading extra context (e.g. a client id to pre-filter) even
 *  before it has a dedicated query-string contract. */
const NAVIGATE_ROUTES: Record<string, (params: Record<string, any>) => string | null> = {
    'customer-profile': (p) => (p?.id != null ? `/customer/${encodeURIComponent(String(p.id))}` : null),
    'customer-debts':   (p) => (p?.id != null ? `/customer/${encodeURIComponent(String(p.id))}` : '/customer/all-debt'),
    'all-debt':         () => '/customer/all-debt',
    'routes-plan':      () => '/routes-plan',
    'open-orders':      () => '/customer/documents/open-orders',
    'canceled-orders':  () => '/customer/documents/canceled-orders',
    'open-invoices':    () => '/customer/documents/open-invoices',
    'all-documents':    () => '/customer/documents/all-documents',
    'scheduled-tasks':  () => '/customer/scheduled-tasks',
    'customer-list':    () => '/customer/customer-list',
    'create-order':     (p) => (p?.id != null ? `/customer/${encodeURIComponent(String(p.id))}/documents/createOrder` : null),
};

/* ──────────────────────────────────────────────────────────────────────── */

const AiDocumentCreatorBridge = () => {
    const dispatch = useDispatch();
    const cache    = useQueryClient();
    const navigate = useNavigate();

    /** `op: 'navigate'` / legacy `op: 'open-orders'` — fire-and-forget. */
    const handleNavigate = useCallback((payload: any) => {
        const targetKey = String(payload?.target || '').trim();
        const build = NAVIGATE_ROUTES[targetKey];
        if (!build) {
            // eslint-disable-next-line no-console
            console.warn('[AiDocumentCreatorBridge] navigate: unknown target', targetKey);
            return;
        }
        const path = build(payload || {});
        if (!path) {
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'לא ניתן לנווט — חסר מזהה לקוח',
                variant: 'alert',
                alertSeverity: 'warning',
            });
            return;
        }
        navigate(path, { state: payload || undefined });
    }, [dispatch, navigate]);

    /** `op: 'open-send-dialog'` — resolve a client's phone (if not given
     *  directly) and open the native WhatsApp composer pre-filled. Bulk
     *  when `payload.clients` has more than one id. Fire-and-forget. */
    const handleOpenSendDialog = useCallback(async (payload: any) => {
        try {
            const clients: string[] = Array.isArray(payload?.clients)
                ? payload.clients.map((c: any) => String(c)).filter(Boolean)
                : [];
            const msg = payload?.msg != null ? String(payload.msg) : undefined;

            if (clients.length > 1) {
                window.dispatchEvent(new CustomEvent('wa-open-bulk', {
                    detail: { clients, msg },
                }));
                return;
            }

            const singleClientId = clients[0]
                ?? (payload?.clientId != null ? String(payload.clientId) : undefined);
            let phone = payload?.phone ? String(payload.phone) : undefined;
            let clientName = payload?.clientName ? String(payload.clientName) : undefined;

            if (!phone && singleClientId) {
                const cid = Number(singleClientId);
                if (Number.isFinite(cid) && cid > 0) {
                    if (!JSDB().getClient(cid).first) {
                        try { await getSpecificCustomersAsync(cid); }
                        catch { /* best effort — fall through, phone lookup below will just fail */ }
                    }
                    const client = JSDB().getClient(cid).first;
                    if (client) {
                        phone = client.getPhoneComplex?.(dateObjectToString(new Date()), 0) || undefined;
                        clientName = clientName || client.name;
                    }
                }
            }

            if (!phone) {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'לא נמצא מספר טלפון ללקוח',
                    variant: 'alert',
                    alertSeverity: 'warning',
                });
                return;
            }

            window.dispatchEvent(new CustomEvent('wa-open-chat', {
                detail: { phone, clientId: singleClientId, clientName, msg },
            }));
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('[AiDocumentCreatorBridge] open-send-dialog failed', err);
        }
    }, [dispatch]);

    /* Preview state — when the iframe asks to open a created document, we
     * fetch it once and stash here. The Dialog mounts at the layout level
     * (top of the React tree) so it sits over every other page. */
    const [previewDoc, setPreviewDoc] = useState<ExtendedDocument | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const openPreview = useCallback(async (payload: any) => {
        const documentId = payload?.documentId;
        const process = payload?.process || payload?.docType || payload?.doc_type || 'order';
        if (documentId == null) return;
        const { type, supplier } = resolvePreviewType(process);
        setPreviewLoading(true);
        try {
            // getSpecificDocument signature:
            //   (type, cid?, fromDate?, toDate?, open?, specific?, full?, ...)
            // Supplier notes need the supplier id as `cid`; customer docs
            // can pass undefined (the loader scopes by id alone).
            const cid = supplier && payload?.entityId
                ? Number(payload.entityId)
                : undefined;
            const docs = await getSpecificDocument(
                type,
                cid,
                undefined, undefined, undefined,
                Number(documentId),
                true,
            );
            const found = (docs || []).find((d) => Number((d as any)?.id) === Number(documentId))
                || (docs || [])[0];
            if (found) {
                setPreviewDoc(found);
            } else {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'המסמך לא נמצא במערכת',
                    variant: 'alert',
                    alertSeverity: 'warning',
                });
            }
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('[AiDocumentCreatorBridge] open-document failed', err);
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'שגיאה בפתיחת המסמך',
                variant: 'alert',
                alertSeverity: 'error',
            });
        } finally {
            setPreviewLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        const handler = async (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            const requestId = String(detail.requestId || '');
            const op = String(detail.op || '');
            if (!requestId) return;
            const sendResult = (
                ok: boolean,
                opts: { documentId?: number | string; error?: string; result?: Record<string, unknown> } = {},
            ) => {
                window.dispatchEvent(new CustomEvent(AI_HOST_BRIDGE_EVENTS.RESULT, {
                    detail: {
                        requestId,
                        ok,
                        documentId: opts.documentId,
                        error: opts.error,
                        result: opts.result,
                    },
                }));
            };

            // ── op: open-in-drawer — fire a generic event; AiTextToDataBridge
            //    (mounted alongside this component) picks it up and opens the
            //    native <TextToDataDrawerAi> pre-selected on process/client.
            //    We DO NOT send a result back — fire-and-forget.
            if (op === 'open-in-drawer') {
                window.dispatchEvent(new CustomEvent('ai-open-text-to-data', {
                    detail: detail.payload,
                }));
                return;
            }

            // ── op: open-document — fetch the created/existing ERP document
            //    and render <DocumentPreview> in a Dialog over the layout.
            //    Fire-and-forget (matches openInDrawer's contract).
            if (op === 'open-document') {
                await openPreview(detail.payload || {});
                return;
            }

            // ── op: navigate / legacy open-orders — push a route for a
            //    named target (see NAVIGATE_ROUTES). Fire-and-forget.
            if (op === 'navigate') {
                handleNavigate(detail.payload || {});
                return;
            }
            if (op === 'open-orders') {
                handleNavigate({ target: 'open-orders', ...(detail.payload || {}) });
                return;
            }

            // ── op: open-send-dialog — open the native WhatsApp composer
            //    pre-filled so a human reviews before sending. Fire-and-forget.
            if (op === 'open-send-dialog') {
                await handleOpenSendDialog(detail.payload || {});
                return;
            }

            // ── op: export-table — export a registered host table (rows
            //    never cross page_context) or a sanitized inline table.
            if (op === 'export-table') {
                try {
                    const result = await exportAiTable((detail.payload || {}) as ExportTablePayload);
                    dispatch({
                        type: SNACKBAR_OPEN,
                        open: true,
                        message: result.truncated
                            ? `יוצאו ${result.rowCount} מתוך ${result.totalRows} שורות (מגבלת בטיחות)`
                            : result.format === 'print' ? 'הטבלה נשלחה להדפסה' : 'הטבלה יוצאה בהצלחה',
                        variant: 'alert',
                        alertSeverity: result.truncated ? 'warning' : 'success',
                    });
                    sendResult(true, { result: result as unknown as Record<string, unknown> });
                } catch (err: any) {
                    dispatch({
                        type: SNACKBAR_OPEN,
                        open: true,
                        message: err?.message || 'ייצוא הטבלה נכשל',
                        variant: 'alert',
                        alertSeverity: 'error',
                    });
                    sendResult(false, { error: err?.message || String(err) });
                }
                return;
            }

            if (op !== 'create-document') return;

            const payload = (detail.payload || {}) as IncomingPayload;

            try {
                if (!payload?.entity?.id) {
                    sendResult(false, { error: 'אין מזהה לקוח / ספק לבקשה' });
                    return;
                }
                if (!payload?.products?.length) {
                    sendResult(false, { error: 'אין שורות מוצר ליצירה' });
                    return;
                }

                const { type, supplier } = resolveDocType(payload.process);
                const dateIso = payload.dateIso || dateObjectToString(new Date());
                const productIds = payload.products
                    .map((p) => Number(p.productId))
                    .filter((id) => Number.isFinite(id) && id > 0);

                const fullProducts = await loadProductDetails(
                    payload.entity.id, dateIso, productIds, supplier,
                );
                const byId = new Map(fullProducts.map((p) => [Number(p.id), p]));

                /* Build IItems in the same order the user edited them in
                 * the compact UI. Skip rows whose productId we couldn't
                 * find in the catalog (defensive — DocsTab already blocks
                 * unmatched rows). */
                const items: IItem[] = [];
                for (let i = 0; i < payload.products.length; i += 1) {
                    const row = payload.products[i];
                    const product = byId.get(Number(row.productId));
                    if (!product) continue;
                    items.push(buildIItem(product, row, i));
                }
                if (!items.length) {
                    sendResult(false, { error: 'לא נמצאו פרטי מוצר במחירון' });
                    return;
                }

                const totals = calcTotals(items);
                const doc = createDocument({
                    id: 0,
                    entId: String(payload.entity.id),
                    entName: payload.entity.name || '',
                    entBName: payload.entity.name || '',
                    date: new Date(dateIso),
                    issueDate: new Date(),
                    type,
                    status: 'open',
                    products: items,
                    comment: `נוצר מצ׳אט AI · ${payload.source?.jobName || ''}`.trim(),
                    mainValue: totals.mainValue,
                    preValue:  totals.preValue,
                    taxValue:  totals.taxValue,
                });

                /* Route to the right creator. `createRealCustomerDocuments`
                 * has explicit branches for every customer doc type AND for
                 * sup_invoice / sup_tax_invoice / sup_refund (line 726 in
                 * api/customer/documents.api.ts), but it does NOT have a
                 * branch for `supplier_note` — it falls into the trailing
                 * `else { reject() }` and the create blows up trying to
                 * deserialize undefined. The existing `NewSupNote` UI uses
                 * a separate `createSupplierNote` function (which is also
                 * what `useCreateSupplierDocument` wraps), so we mirror
                 * the same routing here for supplier delivery / order. */
                const created = (supplier && type === 'supplier_note')
                    ? await createSupplierNote(String(payload.entity.id), doc)
                    : await createRealCustomerDocuments(
                          String(payload.entity.id), doc,
                      );

                /* Mirror what useCreateCustomerDocument's onSuccess does —
                 * invalidate the right query slice and show the snackbar
                 * so the rest of the app refreshes / the user gets a
                 * normal success toast. */
                cache.invalidateQueries(supplier ? SUPPLIER_DOCUMENTS : CUSTOMER_DOCUMENTS);
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'המסמך נוצר בהצלחה מתוך הצ׳אט!',
                    variant: 'alert',
                    alertSeverity: 'success',
                });

                /* `created` is either a single ExtendedDocument
                 * (deserializeDocument path — delivery_certificate, order,
                 * supplier_note, receipt, sup_receipt) OR an ExtendedDocument[]
                 * (docResultsDeserialize path — tax_invoice / invoice / refund
                 * / sup_invoice / sup_tax_invoice / sup_refund / created via
                 * createSupplierNote). Normalise so the "פתח" button gets a
                 * real ERP id back. */
                const createdAny = created as any;
                const createdId = Array.isArray(createdAny)
                    ? createdAny[0]?.id
                    : createdAny?.id;
                // Keep all-debts (and similar) in sync — merge-refresh that client only.
                dispatchAiClientDebtRefresh({
                    clientId: Number(payload.entity.id),
                    supplier,
                });
                sendResult(true, { documentId: createdId ?? undefined });
            } catch (err: any) {
                /* eslint-disable-next-line no-console */
                console.error('[AiDocumentCreatorBridge] create failed', err);
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'אירעה שגיאה ביצירת המסמך מהצ׳אט',
                    variant: 'alert',
                    alertSeverity: 'error',
                });
                sendResult(false, { error: err?.message || String(err) });
            }
        };

        window.addEventListener(AI_HOST_BRIDGE_EVENTS.REQUEST, handler as EventListener);
        return () => window.removeEventListener(AI_HOST_BRIDGE_EVENTS.REQUEST, handler as EventListener);
    }, [dispatch, cache, openPreview, handleNavigate, handleOpenSendDialog]);

    const closePreview = useCallback(() => {
        setPreviewDoc((doc) => {
            if (doc) {
                const entId = Number((doc as any)?.entId ?? (doc as any)?.nkObject?.client_id);
                const t = String((doc as any)?.type || '');
                const isSup = t.startsWith('sup_') || t === 'supplier_note';
                if (Number.isFinite(entId) && entId > 0) {
                    dispatchAiClientDebtRefresh({ clientId: entId, supplier: isSup });
                }
            }
            return null;
        });
    }, []);

    /* Preview dialog — wide enough to fit the full DocumentPreview chrome
     * (it has its own toolbar, signature pad, etc.), same maxWidth as the
     * customer-profile docs table uses. */
    const theme = useTheme();
    const previewOverlayTheme = useMemo(
        () => createTheme(theme, {
            zIndex: {
                ...theme.zIndex,
                // Autocomplete / Menu / Select poppers read theme.zIndex.modal
                // (and keep React theme context through portals).
                modal: AI_DOC_PREVIEW_DIALOG_Z + AI_DOC_PREVIEW_POPPER_OFFSET,
            },
        }),
        [theme],
    );

    return (
        <>
            {previewLoading && (
                /* Lightweight loading scrim while we fetch the doc; the
                 * snackbar shows a final outcome if the load fails. */
                <Dialog open hideBackdrop sx={{ zIndex: AI_DOC_PREVIEW_DIALOG_Z }} PaperProps={{ sx: { p: 2 } }}>
                    טוען מסמך…
                </Dialog>
            )}
            {previewDoc && (
                <Dialog
                    fullWidth
                    open
                    maxWidth="lg"
                    onBackdropClick={closePreview}
                    onClose={closePreview}
                    /* Above the AI iframe's OrderConflictDialog (zIndex 2600)
                     * so "פתח לצפייה" from the conflict picker is actually
                     * visible while the conflict dialog stays open underneath. */
                    sx={{ zIndex: AI_DOC_PREVIEW_DIALOG_Z, '& .MuiDialog-paper': { p: 0 } }}
                >
                    <ThemeProvider theme={previewOverlayTheme}>
                        <DocumentPreview
                            document={previewDoc}
                            creditRecepitValue={false}
                            setCreditRecepitValue={NOOP_SET_BOOL}
                        />
                    </ThemeProvider>
                </Dialog>
            )}
        </>
    );
};

export default AiDocumentCreatorBridge;
